import ReactDOM from 'react-dom'
import { MutableRefObject, ReactNode, useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { IMarkerWithStepName, IStep } from 'src/types'

import { PreviousPage, NextPage } from '../navigation'
import { MapView } from '../map-view'
import {
  ContainerSt,
  MapWrapper,
  OverlayTitleWrapper,
  AccountImageItem,
} from './journey-map.styled'
import { DEFAULT_LANGUAGE, ROUTES } from 'src/constants'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@mui/material'
import { imgSt } from '../bottom-card-title/bottom-card-title.styled'
import { buildProductNavPaths } from 'src/utils'

interface IProps {
  steps: IStep[]
  satelliteView: boolean
  scrollToRef?: MutableRefObject<HTMLSpanElement>
  markerColor?: string
  isImpacts?: boolean
  isTopDivider?: boolean
  accauntId?: string
  navButtonsTopOffset?: number
  mapHeight?: string
  mapMaxHeight?: string
  navVariant?: 'default' | JOURNEY_PAGE_TEMPLATE_ID
  prevLabel?: string
  nextLabel?: string
  navBackgroundColor?: string
  navTextColor?: string
  overlayTitle?: ReactNode
  accountImage?: string
}

export const JourneyMap = (props: IProps) => {
  const { simpleProductId } = useParams()
  const [t, i18n] = useTranslation()
  const location = useLocation()
  const portalContainerRef = useRef(null)

  const productLabel = t('journey-navigation-product-button')
  const journeyLabel = t('journey-navigation-journey-button')

  const markers = useMemo(() => {
    const groupedMarkers: Record<string, IMarkerWithStepName[]> = {}

    props.steps.forEach((step) => {
      step.locations.forEach((location) => {
        if (
          location.latitude !== 0 &&
          location.longitude !== 0 &&
          location.latitude !== null &&
          location.longitude !== null &&
          location.location_address &&
          location.location_address.trim() !== ''
        ) {
          const key = `${location.latitude / 10_000},${location.longitude / 10_000}`

          if (!groupedMarkers[key]) {
            groupedMarkers[key] = []
          }

          groupedMarkers[key].push({
            latitude: location.latitude / 10_000,
            longitude: location.longitude / 10_000,
            stepName: step.name,
            stepLocation: location.location_name,
            stepId: step.id,
          })
        }
      })
    })

    return Object.values(groupedMarkers)
  }, [props.steps])

  if (!portalContainerRef.current) {
    portalContainerRef.current = document.createElement('div')
    portalContainerRef.current.style.cssText = `
      position: absolute;
      top: 15px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999;
      max-width: 800px;
      width: 100%;
    `
    document.body.appendChild(portalContainerRef.current)
  }

  const {
    productPath: productTo,
    impactsPath: impactsTo,
    journeyPath: journeyTo,
  } = buildProductNavPaths(location.pathname)

  return (
    <>
      <ContainerSt
        id="index__components-journey-map__containerst__l78"
        mapHeight={props?.mapHeight}
        mapMaxHeight={props?.mapMaxHeight}
      >
        <MapWrapper
          id="index__components-journey-map__mapwrapper__l79"
          mapHeight={props?.mapHeight}
          mapMaxHeight={props?.mapMaxHeight}
        >
          <MapView
            markerColor={props.markerColor}
            satelliteView={props.satelliteView}
            markers={markers}
            steps={props.steps}
            scrollToRef={props.scrollToRef}
          />

          {(props.overlayTitle || props.accountImage) && (
            <OverlayTitleWrapper id="index__components-journey-map__overlaytitlewrapper__l109">
              {props.accountImage && (
                <AccountImageItem>
                  <Avatar className={imgSt} alt="Account" src={props.accountImage} />
                </AccountImageItem>
              )}
              {props.overlayTitle && (
                <AccountImageItem sx={{ mb: '8px' }}>{props.overlayTitle}</AccountImageItem>
              )}
            </OverlayTitleWrapper>
          )}
        </MapWrapper>
      </ContainerSt>
      {portalContainerRef.current &&
        ReactDOM.createPortal(
          <>
            <PreviousPage
              isTopDivider={props?.isTopDivider}
              isImpactPage
              to={productTo}
              absolute
              topOffset={props?.navButtonsTopOffset}
              variant={props.navVariant ?? 'default'}
              label={props.prevLabel ?? location.state?.previousLabel ?? productLabel}
              backgroundColor={props.navBackgroundColor}
              textColor={props.navTextColor}
            />

            <NextPage
              isTopDivider={props?.isTopDivider}
              isImpactPage
              to={
                props.isImpacts
                  ? impactsTo
                  : i18n.language === DEFAULT_LANGUAGE
                    ? `/${ROUTES.ACCOUNT}/${props?.accauntId}`
                    : `/${i18n.language}/${ROUTES.ACCOUNT}/${props?.accauntId}`
              }
              state={{
                from: journeyTo,
                productId: simpleProductId,
                previousLabel: journeyLabel,
              }}
              topOffset={props?.navButtonsTopOffset}
              variant={props.navVariant ?? 'default'}
              label={props.nextLabel}
              backgroundColor={props.navBackgroundColor}
              textColor={props.navTextColor}
            />
          </>,
          portalContainerRef.current,
        )}
    </>
  )
}
